/**
 * Adding a tile: a report, a number, or a note.
 *
 * "Add report" opens the tile's Configure panel with its Report section empty
 * — pick a custom report or build one there. Adding a report and configuring
 * one are the same panel, so there is one place that knows what a report tile
 * can be. See `TileSettingsSheet`.
 */
import type { ReportModel } from '@clockwise/semantic-core';
import { canVisualize } from '@clockwise/semantic-core';
import { FormulaHint } from '@clockwise/ui';
import { useQuery } from '@tanstack/react-query';
import { FileText, Hash, Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { analyticsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface Props {
  /** Open the Configure panel for a new report tile. */
  onAddReport: () => void;
  onAddMetric: (model: string, measure: string, label: string, trend?: string) => void;
  onAddText: () => void;
  /** True once the definition is at its tile ceiling. */
  full: boolean;
}

export function AddTile({ onAddReport, onAddMetric, onAddText, full }: Props) {
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" disabled={full} onClick={onAddReport}>
        <Plus className="size-4" aria-hidden="true" />
        Add report
      </Button>

      <AddMetric onAdd={onAddMetric} disabled={full} />

      <Button variant="outline" size="sm" onClick={onAddText} disabled={full}>
        <FileText className="size-4" aria-hidden="true" />
        Add note
      </Button>

      {full && (
        <span className="text-xs text-muted-foreground">This dashboard is at its tile limit.</span>
      )}
    </div>
  );
}

/**
 * One number, picked as a subject area plus a measure.
 *
 * That pair IS the whole question — a KPI has no axes and inherits the
 * dashboard's period and filters — so it is composed here rather than in the
 * report builder. Sending somebody to build and save a one-measure report just
 * to show a total would make the reports list a dumping ground for tiles.
 */
function AddMetric({
  onAdd,
  disabled,
}: {
  onAdd: (model: string, measure: string, label: string, trend?: string) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [modelId, setModelId] = useState<string | null>(null);

  const models = useQuery({
    queryKey: queryKeys.analytics.models(),
    queryFn: ({ signal }) => analyticsApi.models(signal).then((r) => r.data),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: open,
  });

  const model = models.data?.find((m) => m.id === modelId);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Back to the subject-area step each time, so reopening never lands on
        // a measure list for a model the author has since stopped thinking about.
        if (!next) setModelId(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Hash className="size-4" aria-hidden="true" />
          Add number
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder={model ? 'Search measures…' : 'Search subject areas…'} />
          <CommandList>
            <CommandEmpty>{models.isPending ? 'Loading…' : 'Nothing to show.'}</CommandEmpty>
            <CommandGroup heading={model ? model.label : 'Subject area'}>
              {!model &&
                (models.data ?? []).map((m) => (
                  <CommandItem key={m.id} value={m.label} onSelect={() => setModelId(m.id)}>
                    <span className="truncate">{m.label}</span>
                  </CommandItem>
                ))}
              {model?.measures.map((measure) => {
                // A KPI is one measure and no axes, and `canVisualize` is
                // what decides whether that shape is drawable — so the list
                // offers only measures that will actually render, with the
                // guard's own sentence on the ones that will not.
                const verdict = canVisualize(
                  model,
                  { measures: [measure.id], rows: [], cols: [] },
                  'metric',
                );
                return (
                  <CommandItem
                    key={measure.id}
                    value={measure.label}
                    disabled={!verdict.ok}
                    // The refusal is a native title; the formula is the ƒ
                    // icon every other measure list uses.
                    title={verdict.ok ? undefined : verdict.reason}
                    onSelect={() => {
                      // TRENDED BY DEFAULT where the model has a time
                      // dimension: the number alone cannot say whether it is
                      // going anywhere, and the history is free — the same
                      // query, grouped. Falls back to the bare value on a
                      // model with no period to group by.
                      onAdd(model.id, measure.id, measure.label, trendDimension(model));
                      setOpen(false);
                      setModelId(null);
                    }}
                  >
                    <span className="truncate">{measure.label}</span>
                    {verdict.ok && measure.formulaText ? (
                      <FormulaHint formula={measure.formulaText} />
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The period a metric should trend over.
 *
 * MONTH BY PREFERENCE, then whatever time dimension the model has. A daily
 * sparkline over a year is 365 points in 44 pixels — a smear, not a shape —
 * and a metric tile is read at a glance rather than measured.
 */
function trendDimension(model: ReportModel): string | undefined {
  const times = model.dimensions.filter((d) => d.isTime);
  return (times.find((d) => d.id === 'month') ?? times[0])?.id;
}
